import React, { useRef, useState } from 'react';
import { FiZoomIn as FiZoomInIcon } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useDeferredMedia } from '../hooks/useDeferredMedia';
import { getScreenshotPreview } from '../lib/screenshotPreview';
import ScreenshotLightbox from './ScreenshotLightbox';
import './ScreenshotDetail.css';

export interface ScreenshotCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ScreenshotDetailProps {
  /** Full original capture; the excerpt is rendered from this file, not a derived asset. */
  src: string;
  /** Crop region in the original 1320x2868 coordinate space. */
  crop: ScreenshotCrop;
  /** Describes the visible excerpt honestly (not a complete screen). */
  alt: string;
  caption?: string;
  /** Accessible label for the full-original trigger. */
  fullImageLabel: string;
  /** Optional fixed stage aspect (e.g. '1320 / 2280') when two excerpts of
      different heights share one slot; the whole selected crop is letterboxed
      below (xMidYMin meet), never clipped or stretched. */
  stageAspectRatio?: string;
  /** Load the excerpt immediately (e.g. the LCP content) instead of deferring
      it until the figure nears the viewport. */
  priority?: boolean;
}

// react-icons v5 types return ReactNode; narrow it for this JSX runtime.
const ZoomIcon = FiZoomInIcon as React.ComponentType<{ size?: number }>;

const ScreenshotDetail: React.FC<ScreenshotDetailProps> = ({
  src,
  crop,
  alt,
  caption,
  fullImageLabel,
  stageAspectRatio,
  priority = false,
}) => {
  const { dsColors, colors } = useTheme();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { ref: figureRef, shouldLoad } = useDeferredMedia<HTMLElement>(priority);
  const isFullFrame =
    crop.x === 0 && crop.y === 0 && crop.width === 1320 && crop.height === 2868;
  const svg = (
    <svg
      className="sd-svg"
      role="img"
      aria-label={alt}
      viewBox={`${crop.x} ${crop.y} ${crop.width} ${crop.height}`}
      overflow="hidden"
      {...(stageAspectRatio
        ? { preserveAspectRatio: 'xMidYMin meet', style: { width: '100%', height: '100%' } }
        : {})}
    >
      {shouldLoad && <image href={getScreenshotPreview(src)} x={0} y={0} width={1320} height={2868} />}
    </svg>
  );
  if (isFullFrame) {
    // The whole original is already displayed; no lightbox affordance.
    return (
      <figure className="sd m-0" ref={figureRef}>
        <div
          className="sd-viewport"
          style={{
            borderColor: colors.border,
            ...(stageAspectRatio ? { aspectRatio: stageAspectRatio } : {}),
          }}
        >
          {svg}
        </div>
        {caption && (
          <figcaption className="mt-3 text-xs" style={{ color: colors.textSecondary }}>
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }
  return (
    <figure className="sd m-0" ref={figureRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={fullImageLabel}
        aria-haspopup="dialog"
        className="sd-viewport sd-trigger focus:outline-none focus-visible:ring-2"
        onClick={() => setOpen(true)}
        style={{
          borderColor: colors.border,
          '--tw-ring-color': dsColors.primary,
          ...(stageAspectRatio ? { aspectRatio: stageAspectRatio } : {}),
        } as React.CSSProperties}
      >
        {svg}
        <span className="sd-zoom" aria-hidden="true">
          <ZoomIcon size={16} />
        </span>
      </button>
      {caption && (
        <figcaption className="mt-3 text-xs" style={{ color: colors.textSecondary }}>
          {caption}
        </figcaption>
      )}
      {open && (
        <ScreenshotLightbox
          src={src}
          alt={alt}
          caption={caption}
          triggerRef={triggerRef}
          onClose={() => setOpen(false)}
        />
      )}
    </figure>
  );
};

export default ScreenshotDetail;
